import { useCallback, useState } from 'react';
import { useCurrentUser } from '../../auth';

/**
 * Return type for `useLocationFilter`.
 *
 * Provides everything a page needs to render a location filter Select
 * that respects the user's preferred location and saved locations.
 */
export interface UseLocationFilterResult {
  /** Currently selected location code, or `undefined` for "all". */
  readonly value: string | undefined;
  /** Update the filter. Pass `undefined` to clear (show all). */
  readonly setValue: (next: string | undefined) => void;
  /** Reset to the user's preferred location (or undefined if none). */
  readonly reset: () => void;
  /**
   * Location codes available as filter options. These are the user's
   * saved locations for the active BU. If the user hasn't saved any,
   * this is an empty array — the consumer should fall back to the full
   * BU location list or hide the filter.
   */
  readonly options: readonly string[];
  /**
   * Whether the user has a preferred location set. Useful for showing
   * a "reset to default" affordance when the filter has been changed.
   */
  readonly hasPreferred: boolean;
}

/**
 * Shared hook for location filter state.
 *
 * Initializes the filter to the user's preferred location (from
 * `useCurrentUser().preferredLocation`). Options are scoped to the
 * user's saved locations for the active BU.
 *
 * Usage:
 * ```tsx
 * const locationFilter = useLocationFilter();
 * // Render a Select with locationFilter.options, value, setValue
 * // Pass locationFilter.value to your data query
 * ```
 *
 * This hook does NOT render any UI — it only manages state. The
 * consuming page decides how to present the filter (Select, chips,
 * toggle, etc.) using Stencil components.
 */
export function useLocationFilter(): UseLocationFilterResult {
  const { preferredLocation, savedLocations } = useCurrentUser();
  const [value, setValueState] = useState<string | undefined>(preferredLocation);

  // Follow preference changes made while this page is mounted (or kept
  // alive by the router): when the preferred location changes on the
  // profile page, the filter tracks it. Render-time derived-state
  // adjustment — no effect, no extra commit.
  const [prevPreferred, setPrevPreferred] = useState(preferredLocation);
  if (preferredLocation !== prevPreferred) {
    setPrevPreferred(preferredLocation);
    setValueState(preferredLocation);
  }

  const setValue = useCallback((next: string | undefined) => setValueState(next), []);

  const reset = useCallback(() => setValueState(preferredLocation), [preferredLocation]);

  return {
    value,
    setValue,
    reset,
    options: savedLocations,
    hasPreferred: !!preferredLocation,
  };
}
